import React, { useEffect, useState, useRef } from "react";
import Lenis from "lenis";
import {
  TrendingUp,
  Handshake,
  ArrowRight,
  ChevronRight,
  CheckCircle2,
  MapPin,
  Phone,
  Mail,
  Lock,
  ChevronLeft,
  X,
  Cpu,
  ShieldCheck,
  Terminal,
  Activity,
  DollarSign,
  MessageCircle,
  Building2,
  Store,
  Star,
  Landmark,
  Briefcase,
  User
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Toaster, toast } from "@/components/ui/sonner";
import "@/App.css";

// --- PROGRAM OFFERINGS DATA (mirrors fredrickalphonso.com live offers) ---
const OFFERINGS = [
  {
    id: "business",
    title: "Four-Page Business Website",
    audience: "MSMEs · Builders · Developers · Real Estate Professionals",
    price: "₹7,000",
    period: "+ GST / year",
    badge: "Starter",
    description: "Domain, hosting, design and initial content for Home, About Us, Services and Contact Us — a complete professional presence.",
    features: [
      "Your own domain name",
      "Hosting included",
      "Modern mobile-friendly design",
      "Enquiry-focused page structure",
      "WhatsApp and call buttons on every page"
    ],
    checkoutUrl: "https://fredrickalphonso.com/website-payment/?plan=business#payment"
  },
  {
    id: "bulk",
    title: "15 Projects. 15 Websites.",
    audience: "Builders & Developers with project pipelines",
    price: "₹75,000",
    period: "+ GST / year",
    badge: "Best Value",
    description: "Only ₹5,000 + GST per project website, including domain, hosting, design and initial content. Built for developers launching multiple projects.",
    features: [
      "15 independent project websites",
      "Domain + hosting per project",
      "Consistent brand system",
      "Buyer enquiry buttons per project",
      "Priority build queue"
    ],
    checkoutUrl: "https://fredrickalphonso.com/website-payment/?plan=bulk#payment"
  },
  {
    id: "personal",
    title: "Your Name. Your Legacy.",
    audience: "Celebrities · Entrepreneurs · Public Figures",
    price: "₹7,000",
    period: "All inclusive, even GST",
    badge: "Signature",
    description: "A distinguished single-page name portfolio with domain, hosting and professional presentation — yourname.com, built to impress.",
    features: [
      "Personal name domain",
      "Single-page portfolio design",
      "Media & profile sections",
      "Enquiry routing built-in",
      "Delivered launch-ready"
    ],
    checkoutUrl: "https://fredrickalphonso.com/website-payment/?plan=personal#payment"
  }
];

export default function App() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formStep, setFormStep] = useState(1);

  // --- LEAD SCORING DASHBOARD STATES ---
  const [leadVolume, setLeadVolume] = useState(150);
  const [budgetTier, setBudgetTier] = useState("mid");
  const [channelMix, setChannelMix] = useState("social");
  const [reraRegistered, setReraRegistered] = useState(true);
  const [instantCrmSync, setInstantCrmSync] = useState(true);

  // Form Inputs
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "Mumbai MMR",
    category: "broker",
    timeline: "Immediate"
  });

  // 3D Parallax Mouse Tracking for Hero Card
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);

  useEffect(() => {
    // Lenis Smooth Momentum Scrolling
    try {
      const lenis = new Lenis({
        duration: 1.4,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
      });

      let rafId;
      function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);

      return () => {
        cancelAnimationFrame(rafId);
        lenis.destroy();
      };
    } catch (e) {
      console.error("Lenis smooth scroll failed to initialize:", e);
    }
  }, []);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setMousePosition({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 0, y: 0 });
  };

  const openForm = (cat = "broker") => {
    setFormData(prev => ({ ...prev, category: cat }));
    setFormStep(1);
    setIsFormOpen(true);
  };

  // --- LEAD VELOCITY SCORING COMPUTATIONS (client-side simulation) ---
  const calculateLeadScore = () => {
    let score = 40;

    if (leadVolume > 300) score += 15;
    else if (leadVolume > 100) score += 10;
    else score += 5;

    if (budgetTier === "super-sobo") score += 20;
    else if (budgetTier === "luxury") score += 15;
    else if (budgetTier === "mid") score += 10;
    else score += 5;

    if (channelMix === "google") score += 10;
    else if (channelMix === "social") score += 8;
    else score += 4;

    if (reraRegistered) score += 10;
    if (instantCrmSync) score += 10;

    return Math.min(score, 100);
  };

  const leadScore = calculateLeadScore();

  const getScoreVerdict = (s) => {
    if (s >= 90) return { label: "CRITICAL SYNERGY - VELOCITY MAX", color: "text-sky-700 border-sky-300 bg-sky-50", desc: "Your system has minimal pipeline friction. A strong foundation for scaling buyer enquiries immediately." };
    if (s >= 75) return { label: "OPTIMIZED - HIGH CONVERSION POTENTIAL", color: "text-cyan-700 border-cyan-300 bg-cyan-50", desc: "Well-optimized lead generation pipelines. Slight friction exists in lead routing and response timing." };
    return { label: "FRAGILE PIPELINE - ACCELERATION NEEDED", color: "text-amber-700 border-amber-300 bg-amber-50", desc: "Limited digital integration. Enquiries are susceptible to leakage and competitor capture." };
  };

  const verdict = getScoreVerdict(leadScore);

  // --- SECURE CHECKOUT (links to the creator's live payment page) ---
  // window.open must fire synchronously inside the click handler or popup blockers intercept it
  const triggerSecureCheckout = (offering) => {
    window.open(offering.checkoutUrl, "_blank", "noopener,noreferrer");
    toast.success("OPENING SECURE CHECKOUT", {
      description: "You are being taken to our secured payment page to complete your order.",
      duration: 2500
    });
  };

  // --- LEAD FORM: delivers via visitor's own email client (mailto:) ---
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Contact details missing", {
        description: "Please complete step 1 before generating your lead score.",
      });
      return;
    }
    const subject = `New Website Enquiry — ${formData.name} (Score ${leadScore}/100)`;
    const body = [
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Phone / WhatsApp: ${formData.phone}`,
      `Region: ${formData.city}`,
      `Category: ${formData.category}`,
      `Timeline: ${formData.timeline}`,
      `Pipeline Velocity Score: ${leadScore}/100`,
      "",
      "Sent from the AEMS Marketing lead diagnostics page."
    ].join("\n");
    window.location.href = `mailto:ceo@aemsmarketing.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setFormStep(4);
    toast.success("Diagnostics complete!", {
      description: `Your enquiry has been prepared for ${formData.name} — press send in your email app.`,
    });
  };

  return (
    <div className="relative min-h-screen selection:bg-sky-200 selection:text-sky-900 bg-sky-50 text-slate-800">
      <Toaster position="top-right" richColors />

      {/* --- SKY BLUE BACKGROUND GRID --- */}
      <div className="absolute top-0 left-0 w-full h-[800px] pointer-events-none -z-10 sky-grid" />

      {/* --- HEAD NAVIGATION --- */}
      <header>
        <nav
          className="sticky top-0 z-50 border-b border-sky-100 bg-white/85 backdrop-blur-md transition-all duration-300 shadow-sm"
          data-testid="navbar"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-gradient-to-tr from-sky-600 to-cyan-400 rounded-lg flex items-center justify-center shadow-md sky-glow">
                <Terminal className="h-5 w-5 text-white shrink-0" />
              </div>
              <div>
                <div className="flex items-baseline space-x-2">
                  <span className="text-slate-900 font-black text-xl tracking-tight block leading-tight font-mono uppercase">
                    AEMS <span className="text-sky-500 font-semibold text-lg">Marketing</span>
                  </span>
                  <span className="h-2 w-2 rounded-full bg-sky-500 animate-ping" />
                </div>
                <span className="text-[10px] uppercase font-mono tracking-widest text-slate-400 block leading-none">
                  Est. 2016 • Founder: Fredrick Alphonso
                </span>
              </div>
            </div>

            <div className="hidden lg:flex items-center space-x-8 text-xs font-mono font-bold tracking-widest uppercase text-slate-500">
              <a href="#about" className="hover:text-sky-600 transition-colors">About</a>
              <a href="#work" className="hover:text-sky-600 transition-colors">Who We Serve</a>
              <a href="#lead-dashboard" className="hover:text-sky-600 transition-colors">Lead Scoring</a>
              <a href="#schemes" className="hover:text-sky-600 transition-colors">Offers</a>
              <a href="#faq" className="hover:text-sky-600 transition-colors">FAQ</a>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => openForm("broker")}
                className="bg-sky-500 hover:bg-sky-600 text-white px-5 py-2.5 rounded-lg text-xs font-bold font-mono uppercase tracking-wider transition-all duration-200 shadow-md shadow-sky-500/25 hover:shadow-sky-500/40"
                data-testid="nav-get-started-button"
              >
                Get Score Diagnostics
              </button>
            </div>
          </div>
        </nav>
      </header>

      <main>
        {/* --- HERO CORE SECTION --- */}
        <section className="relative overflow-hidden max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-16 pb-24 md:py-28 lg:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

            {/* Hero Left: Micro Title and Copy */}
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center space-x-2 bg-white border border-sky-200 rounded-full py-1.5 px-3.5 shadow-sm">
                <Cpu className="h-4 w-4 text-sky-500 animate-spin" />
                <span className="text-[9px] uppercase font-mono tracking-widest text-sky-600 font-bold">
                  WEBSITES BUILT TO IMPRESS. DESIGNED TO PERFORM.
                </span>
              </div>

              {/* Giant uppercase Title */}
              <div className="space-y-3">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] font-sans text-slate-900 uppercase">
                  EMPOWERING THE <br />
                  <span className="text-sky-500 sky-text-glow font-extrabold">REAL ESTATE</span> <br />
                  ECOSYSTEM.
                </h1>
              </div>

              <p className="text-slate-500 text-sm sm:text-base max-w-xl font-mono leading-relaxed">
                We design and develop modern, high-performing websites for Builders, Developers, Real Estate Professionals, MSMEs, Retailers and Personal Brands — lead-focused, mobile responsive and built to turn visitors into genuine business enquiries.
              </p>

              {/* CTA controls */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                <a
                  href="#lead-dashboard"
                  className="bg-sky-500 hover:bg-sky-600 text-white text-xs font-mono font-black uppercase tracking-widest py-4 px-8 rounded-lg shadow-lg shadow-sky-500/20 hover:shadow-sky-500/30 transition-all duration-300 text-center flex items-center justify-center space-x-2"
                  data-testid="hero-get-started-button"
                >
                  <span>Launch Diagnostics Dashboard</span>
                  <ArrowRight className="h-4 w-4 text-white shrink-0" />
                </a>
                <a
                  href="#schemes"
                  className="border border-slate-300 hover:border-sky-400 bg-white/70 text-slate-600 text-xs font-mono font-bold uppercase tracking-widest py-4 px-8 rounded-lg transition-all duration-300 text-center flex items-center justify-center space-x-2"
                  data-testid="hero-explore-manifesto-button"
                >
                  <span>View Offers & Pricing</span>
                </a>
              </div>

              {/* Compliance trust details */}
              <div className="pt-6 border-t border-sky-100 max-w-lg">
                <span className="text-[9px] font-mono uppercase tracking-widest text-slate-400 font-bold block mb-3">
                  WHY BUSINESSES CHOOSE AEMS
                </span>
                <div className="flex flex-wrap gap-y-2 gap-x-6 text-[11px] font-mono text-slate-500">
                  <span className="flex items-center space-x-1">
                    <ShieldCheck className="h-4 w-4 text-sky-500 mr-1 shrink-0" />
                    GST-Clear Pricing
                  </span>
                  <span className="flex items-center space-x-1">
                    <Activity className="h-4 w-4 text-sky-500 mr-1 shrink-0 animate-pulse" />
                    Lead-Focused Design
                  </span>
                  <span className="flex items-center space-x-1">
                    <Lock className="h-4 w-4 text-sky-500 mr-1 shrink-0" />
                    Secured Cashfree Checkout
                  </span>
                </div>
              </div>
            </div>

            {/* Hero Right: 3D Parallax Telemetry Card with Fredrick Alphonso's Portrait */}
            <div className="lg:col-span-5 flex justify-center">
              <div
                ref={cardRef}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                style={{
                  transform: `rotateX(${mousePosition.y * -15}deg) rotateY(${mousePosition.x * 15}deg)`,
                  transformPerspective: 1000
                }}
                className="relative w-full max-w-[420px] aspect-[4/5] rounded-2xl bg-white text-slate-800 p-6 shadow-2xl shadow-sky-200/60 overflow-hidden group cursor-pointer border border-sky-200 transition-transform duration-100"
                data-testid="hero-3d-parallax-card"
              >
                {/* Fredrick Alphonso's Portrait Backdrop */}
                <div className="absolute inset-0 z-0 opacity-70 group-hover:opacity-85 transition-opacity duration-500">
                  <img
                    src="https://fredrickalphonso.com/wp-content/plugins/aems-cp-landing/assets/hero.jpg"
                    alt="Fredrick Alphonso — Founder and Business Director, AEMS Marketing"
                    className="w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700"
                  />
                </div>

                {/* Glowing sky spotlight overlay */}
                <div
                  className="absolute inset-0 pointer-events-none z-10 transition-opacity duration-300 opacity-0 group-hover:opacity-100"
                  style={{
                    background: `radial-gradient(circle 220px at ${(mousePosition.x + 0.5) * 100}% ${(mousePosition.y + 0.5) * 100}%, rgba(14, 165, 233, 0.20) 0%, transparent 80%)`
                  }}
                />

                {/* Foreground details */}
                <div className="relative z-20 h-full flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1 bg-white/85 backdrop-blur-sm rounded-lg px-3 py-2">
                      <span className="text-[9px] font-mono tracking-widest uppercase text-sky-600 font-bold block">
                        FOUNDER & BUSINESS DIRECTOR
                      </span>
                      <h2 className="text-lg font-bold font-mono tracking-tight text-slate-900 uppercase">
                        Fredrick Alphonso
                      </h2>
                    </div>
                    <span className="h-2 w-2 rounded-full bg-sky-500 animate-ping" />
                  </div>

                  <div className="space-y-4">
                    <div className="bg-white/90 border border-sky-100 backdrop-blur-md rounded-xl p-4 space-y-1 shadow-sm">
                      <span className="text-[9px] font-mono text-slate-500 tracking-wider uppercase block">
                        Team Experience
                      </span>
                      <div className="flex justify-between items-baseline">
                        <span className="text-xl font-bold font-mono tracking-tight text-sky-600">
                          30+ Years
                        </span>
                        <span className="text-xs text-cyan-600 font-mono font-bold flex items-center">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          Sales & Marketing
                        </span>
                      </div>
                    </div>

                    <div className="bg-white/90 border border-sky-100 backdrop-blur-md rounded-xl p-4 space-y-1 shadow-sm">
                      <span className="text-[9px] font-mono text-slate-500 tracking-wider uppercase block">
                        Operating Base
                      </span>
                      <div className="flex justify-between items-baseline">
                        <span className="text-xl font-bold font-mono tracking-tight text-slate-900">
                          Mumbai, Est. 2016
                        </span>
                        <span className="text-xs text-sky-600 font-mono">
                          Direct Support
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/85 backdrop-blur-sm rounded-lg px-3 py-2 flex justify-between items-center text-[10px] text-slate-500 font-mono">
                    <span>WORDPRESS • MOBILE RESPONSIVE</span>
                    <span className="font-semibold text-slate-900 flex items-center space-x-1">
                      <span>Lead Focused</span>
                      <ChevronRight className="h-3 w-3 text-sky-500" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* --- CONTINUOUS RUNNING MARQUEE --- */}
        <div className="relative border-y border-sky-200 bg-sky-600 py-5 overflow-hidden z-10">
          <div className="animate-marquee flex items-center space-x-12 whitespace-nowrap text-white">
            {[1, 2].map((num) => (
              <div key={num} className="flex items-center space-x-12 font-mono">
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-white">
                  ⚡ EMPOWERING REAL ESTATE ECOSYSTEM
                </span>
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-sky-200">
                  ⚡ 30+ YEARS SALES & MARKETING EXPERIENCE
                </span>
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-white">
                  ⚡ FOUNDED BY FREDRICK ALPHONSO 2016
                </span>
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-sky-200">
                  ⚡ WEBSITES FOR BUILDERS, DEVELOPERS & REAL ESTATE PROFESSIONALS
                </span>
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-white">
                  ⚡ SECURED CASHFREE CHECKOUT
                </span>
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-sky-200">
                  ⚡ MUMBAI-BASED · DIRECT WHATSAPP SUPPORT
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* --- FOUNDER'S BIOGRAPHY & MISSION --- */}
        <section id="about" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

            {/* Bio Left Column: Portrait */}
            <div className="lg:col-span-5 relative group">
              <div className="relative rounded-2xl overflow-hidden border border-sky-200 shadow-xl shadow-sky-100 aspect-[4/3] bg-white">
                <img
                  src="https://fredrickalphonso.com/wp-content/plugins/aems-cp-landing/assets/about.jpg"
                  alt="Fredrick Alphonso — professional business portrait, AEMS Marketing Mumbai"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-sky-950/60 via-transparent to-transparent" />

                <div className="absolute bottom-6 left-6 right-6 bg-white/85 backdrop-blur-md border border-sky-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
                  <div>
                    <span className="text-[9px] font-mono tracking-widest text-sky-600 font-bold block uppercase">
                      BUILT BY REAL ESTATE SALES PROFESSIONALS
                    </span>
                    <span className="text-xs font-bold font-mono text-slate-900">
                      Mumbai-based · Real estate focused
                    </span>
                  </div>
                  <span className="text-[10px] font-mono text-slate-500">Since 2016</span>
                </div>
              </div>
            </div>

            {/* Bio Right Column: Detailed Copy */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs uppercase font-mono tracking-[0.25em] text-sky-600 font-bold block">
                FOUNDER PROFILE & INTENT
              </span>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-none uppercase text-slate-900 font-sans">
                ABOUT FREDRICK ALPHONSO
              </h2>
              <p className="text-slate-600 text-sm sm:text-base font-mono leading-relaxed">
                As the founder and business director of AEMS Marketing, established in 2016, Fredrick Alphonso bridges deep real estate sales experience with modern website design and development capability.
              </p>
              <p className="text-slate-500 text-sm font-mono leading-relaxed">
                Our team brings over 30 years of experience in sales and marketing, including real estate sales and buyer acquisition. We build websites around what actually brings you enquiries — not just good looks. Whether you are a builder showcasing a development, a real estate professional generating property enquiries, an MSME establishing credibility or a personal brand building authority, your website should represent the quality of your business.
              </p>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-sky-100">
                <div className="space-y-1">
                  <span className="text-[9px] font-mono text-slate-400 uppercase block">Operating Since</span>
                  <span className="text-lg font-bold font-mono text-sky-600">2016 (Mumbai)</span>
                </div>
                <div className="space-y-1">
                  <span className="text-[9px] font-mono text-slate-400 uppercase block">Credentials</span>
                  <span className="text-lg font-bold font-mono text-slate-900">Digital Marketing Certified</span>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* --- WHO WE SERVE: WEBSITES DESIGNED AROUND YOUR BUSINESS --- */}
        <section id="work" className="bg-white border-y border-sky-100 py-20 lg:py-32" data-testid="who-we-serve-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">

            <div className="space-y-4 max-w-3xl mb-16 lg:mb-24">
              <div className="inline-flex items-center space-x-2 bg-sky-50 border border-sky-200 rounded-full py-1 px-3 shadow-sm">
                <span className="h-2 w-2 rounded-full bg-sky-500" />
                <span className="text-[9px] font-mono tracking-widest text-sky-600 font-bold uppercase">
                  VIEW OUR WORK BY BUSINESS TYPE
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-none uppercase text-slate-900 font-sans">
                Websites Designed Around Your Business
              </h2>
              <p className="text-slate-500 text-sm font-mono leading-relaxed">
                Every AEMS website is shaped around what your customers need to see — and what brings you genuine enquiries. Pick your world below.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "Builders", desc: "Showcase projects, strengthen credibility and generate buyer enquiries.", icon: Building2, testid: "serve-builders" },
                { title: "Developers", desc: "Present your company, project portfolio, pipeline and capabilities professionally.", icon: Briefcase, testid: "serve-developers" },
                { title: "Real Estate Professionals", desc: "Create a recognisable brand, organise projects and make it easy for buyers to contact you.", icon: User, testid: "serve-realtors" },
                { title: "Landowners", desc: "Present land opportunities clearly to serious builders, developers and JV partners.", icon: Landmark, testid: "serve-landowners" },
                { title: "MSMEs & Retailers", desc: "Build trust, explain your offering and make customer enquiries easier.", icon: Store, testid: "serve-msmes" },
                { title: "Personal Brands & Celebrities", desc: "Create a polished digital home for your work, profile, media and enquiries.", icon: Star, testid: "serve-personal-brands" }
              ].map((audience) => {
                const IconComponent = audience.icon;
                return (
                  <div
                    key={audience.testid}
                    className="bg-sky-50/60 border border-sky-100 rounded-2xl p-7 space-y-4 hover:border-sky-400 hover:bg-white hover:shadow-lg hover:shadow-sky-100 transition-all duration-300 group"
                    data-testid={audience.testid}
                  >
                    <div className="h-11 w-11 rounded-xl bg-sky-100 group-hover:bg-sky-500 flex items-center justify-center text-sky-600 group-hover:text-white transition-colors duration-300">
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <div className="space-y-2 font-mono">
                      <h3 className="text-sm font-bold tracking-widest text-slate-900 uppercase">
                        {audience.title}
                      </h3>
                      <p className="text-xs text-slate-500 leading-relaxed font-medium">
                        {audience.desc}
                      </p>
                    </div>
                    <a
                      href="#schemes"
                      className="inline-flex items-center space-x-1 text-[10px] font-mono font-bold uppercase tracking-widest text-sky-600 hover:text-sky-700"
                    >
                      <span>See matching offer</span>
                      <ChevronRight className="h-3 w-3" />
                    </a>
                  </div>
                );
              })}
            </div>

          </div>
        </section>

        {/* --- REAL-ESTATE LEAD SCORING DASHBOARD --- */}
        <section id="lead-dashboard" className="bg-gradient-to-b from-sky-100 to-sky-50 text-slate-800 py-20 lg:py-32 relative border-y border-sky-200" data-testid="lead-scoring-dashboard">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] pointer-events-none opacity-20 bg-[radial-gradient(circle_at_center,_#0EA5E9_0%,_transparent_70%)]" />

          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative z-10">

            <div className="space-y-4 max-w-3xl mb-16 lg:mb-24">
              <div className="inline-flex items-center space-x-2 bg-white border border-sky-200 rounded-full py-1 px-3 shadow-sm">
                <span className="h-2 w-2 rounded-full bg-sky-500 animate-pulse" />
                <span className="text-[9px] font-mono tracking-widest text-sky-600 font-bold uppercase">
                  DASHBOARD METRICS INTERFACE
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight uppercase font-sans text-slate-900">
                Real Estate Lead Velocity Diagnostics
              </h2>
              <p className="text-slate-500 text-sm font-mono leading-relaxed max-w-2xl">
                Simulate your lead pipeline parameters below. Our indicative algorithm maps budget profiles, digital integrations and traffic channels to generate your pipeline quality velocity score. Runs entirely in your browser — nothing is stored.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-stretch">

              {/* Left Controls */}
              <div className="lg:col-span-7 bg-white/80 border border-sky-200 rounded-2xl p-6 sm:p-8 space-y-6 flex flex-col justify-between shadow-lg shadow-sky-100">

                <div className="space-y-6">

                  {/* Slider: Raw Volume */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-baseline">
                      <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest block">
                        Raw Expected Lead Volume / Month
                      </span>
                      <span className="text-base font-black text-sky-600 font-mono" data-testid="lead-volume-value">
                        {leadVolume} Leads
                      </span>
                    </div>
                    <input
                      type="range"
                      min={10}
                      max={1000}
                      step={10}
                      value={leadVolume}
                      onChange={(e) => setLeadVolume(Number(e.target.value))}
                      className="w-full h-1.5 bg-sky-200 rounded-lg appearance-none cursor-pointer accent-sky-500"
                      data-testid="lead-volume-slider"
                    />
                    <div className="flex justify-between text-[9px] text-slate-400 font-mono">
                      <span>10 Leads</span>
                      <span>1,000 Leads</span>
                    </div>
                  </div>

                  {/* Budget tier selector */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest block">Average Budget Profile</label>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { id: "affordable", label: "Under ₹1Cr" },
                        { id: "mid", label: "₹1Cr - ₹3Cr" },
                        { id: "luxury", label: "₹3Cr - ₹10Cr" },
                        { id: "super-sobo", label: "₹10Cr+ SOBO" }
                      ].map((bt) => (
                        <button
                          key={bt.id}
                          type="button"
                          onClick={() => setBudgetTier(bt.id)}
                          className={`py-2 px-1 rounded-lg text-[10px] font-mono font-bold border transition-all duration-200 ${
                            budgetTier === bt.id
                              ? "bg-sky-100 border-sky-500 text-sky-700"
                              : "bg-white border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-sky-50"
                          }`}
                          data-testid={`budget-tier-${bt.id}`}
                        >
                          {bt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Marketing Channels */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest block">Primary Acquisition Channel</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: "social", label: "Social Campaigns" },
                        { id: "google", label: "Google SEM Search" },
                        { id: "organic", label: "SEO & Portals" }
                      ].map((ch) => (
                        <button
                          key={ch.id}
                          type="button"
                          onClick={() => setChannelMix(ch.id)}
                          className={`py-2 px-1 rounded-lg text-[10px] font-mono font-bold border transition-all duration-200 ${
                            channelMix === ch.id
                              ? "bg-sky-100 border-sky-500 text-sky-700"
                              : "bg-white border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-sky-50"
                          }`}
                          data-testid={`channel-${ch.id}`}
                        >
                          {ch.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Integration checklist */}
                  <div className="space-y-3 pt-2">
                    <label className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest block">Pipeline Diagnostics</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                      <button
                        type="button"
                        onClick={() => setReraRegistered(!reraRegistered)}
                        className={`flex items-center justify-between p-3 rounded-xl border text-xs font-mono font-bold transition-all duration-200 ${
                          reraRegistered
                            ? "bg-sky-50 border-sky-400 text-slate-900"
                            : "bg-white/60 border-slate-200 text-slate-400"
                        }`}
                        data-testid="rera-toggle-button"
                      >
                        <span>Own Website Live</span>
                        <CheckCircle2 className={`h-4 w-4 ${reraRegistered ? "text-sky-500" : "text-slate-300"}`} />
                      </button>

                      <button
                        type="button"
                        onClick={() => setInstantCrmSync(!instantCrmSync)}
                        className={`flex items-center justify-between p-3 rounded-xl border text-xs font-mono font-bold transition-all duration-200 ${
                          instantCrmSync
                            ? "bg-sky-50 border-sky-400 text-slate-900"
                            : "bg-white/60 border-slate-200 text-slate-400"
                        }`}
                        data-testid="crm-sync-toggle-button"
                      >
                        <span>Instant WhatsApp Response</span>
                        <CheckCircle2 className={`h-4 w-4 ${instantCrmSync ? "text-sky-500" : "text-slate-300"}`} />
                      </button>

                    </div>
                  </div>

                </div>

                <div className="pt-4 border-t border-sky-100 flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => openForm("broker")}
                    className="flex-1 bg-sky-500 hover:bg-sky-600 text-white font-mono font-bold text-xs uppercase tracking-widest py-3.5 rounded-lg shadow-md shadow-sky-500/25 transition-colors flex items-center justify-center space-x-2"
                    data-testid="dashboard-submit-button"
                  >
                    <span>Submit Score & Request Consultation</span>
                    <ArrowRight className="h-4 w-4 text-white shrink-0" />
                  </button>
                </div>

              </div>

              {/* Right Diagnostic Real-time Feedback panel */}
              <div className="lg:col-span-5 bg-white/70 border border-sky-200 rounded-2xl p-6 sm:p-8 flex flex-col justify-between space-y-8 relative overflow-hidden shadow-lg shadow-sky-100">
                <div className="absolute top-0 left-0 w-full h-1 bg-sky-500" />

                <div className="space-y-4">
                  <div className="flex justify-between items-center font-mono">
                    <span className="text-[10px] uppercase text-slate-500 font-bold block tracking-widest">DIAGNOSTIC SIGNAL</span>
                    <span className="text-[9px] bg-sky-100 text-sky-700 px-2.5 py-1 rounded-full font-bold">ALGORITHM ONLINE</span>
                  </div>

                  {/* Score readout */}
                  <div className="text-center py-6 border-y border-sky-100 space-y-2">
                    <span className="text-[9px] font-mono text-slate-400 block uppercase tracking-widest">PIPELINE VELOCITY INDEX</span>
                    <div className="text-5xl font-black font-mono text-slate-900 tracking-tighter" data-testid="lead-score-value">
                      {leadScore} <span className="text-xs font-normal text-sky-500">/ 100</span>
                    </div>
                    <span className={`inline-block text-[10px] font-mono font-bold border rounded-full px-3 py-1 mt-1 ${verdict.color}`} data-testid="lead-score-verdict">
                      {verdict.label}
                    </span>
                  </div>

                  <p className="text-xs font-mono text-slate-500 leading-relaxed text-center py-2">
                    {verdict.desc}
                  </p>
                </div>

                {/* Process image */}
                <div className="relative rounded-xl overflow-hidden aspect-[16/9] border border-sky-200">
                  <img
                    src="https://fredrickalphonso.com/wp-content/plugins/aems-cp-landing/assets/process.jpg"
                    alt="Website planning and real estate project work at AEMS Marketing"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-sky-950/60 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 flex justify-between items-center w-[calc(100%-24px)] font-mono text-[9px]">
                    <span className="text-white font-bold">AEMS Discovery → Launch</span>
                    <span className="text-sky-300">5-Step Process</span>
                  </div>
                </div>

              </div>

            </div>

          </div>
        </section>

        {/* --- PROGRAMS, SCHEMES & SECURE CHECKOUT --- */}
        <section id="schemes" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-32" data-testid="offers-section">

          <div className="space-y-4 max-w-3xl mb-16 lg:mb-24">
            <div className="inline-flex items-center space-x-2 bg-white border border-sky-200 rounded-full py-1 px-3 shadow-sm">
              <span className="h-2 w-2 rounded-full bg-sky-500" />
              <span className="text-[9px] font-mono tracking-widest text-sky-600 font-bold uppercase">
                LIMITED PROFESSIONAL WEBSITE OFFERS
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-none uppercase text-slate-900 font-sans">
              START YOUR WEBSITE PROJECT TODAY
            </h2>
            <p className="text-slate-500 text-sm font-mono leading-relaxed">
              Simple, clear and professional: choose your offer, send the short form, confirm the scope, pay by verified secure checkout, and we build and deliver. Tap a card below to open the secured payment page.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {OFFERINGS.map((offering) => (
              <div
                key={offering.id}
                className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 flex flex-col justify-between hover:border-sky-400 hover:shadow-xl hover:shadow-sky-100 transition-all duration-300 relative group"
              >
                {/* Badge */}
                <div className="absolute top-4 right-4 bg-sky-100 text-sky-700 text-[9px] font-bold font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border border-sky-200">
                  {offering.badge}
                </div>

                <div className="space-y-6">
                  <div className="space-y-1.5">
                    <span className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold pr-24">
                      {offering.audience}
                    </span>
                    <h3 className="text-lg font-bold font-mono tracking-tight text-slate-900 uppercase group-hover:text-sky-600 transition-colors duration-300">
                      {offering.title}
                    </h3>
                  </div>

                  <div className="flex items-baseline space-x-2 border-y border-slate-100 py-4">
                    <span className="text-2xl font-black text-slate-900 font-mono tracking-tight">
                      {offering.price}
                    </span>
                    <span className="text-xs text-slate-400 font-mono">
                      / {offering.period}
                    </span>
                  </div>

                  <p className="text-xs font-mono text-slate-500 leading-relaxed">
                    {offering.description}
                  </p>

                  <ul className="space-y-3 pt-2">
                    {offering.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-start text-xs text-slate-500 font-mono">
                        <CheckCircle2 className="h-4 w-4 text-sky-500 shrink-0 mr-2 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-8">
                  <button
                    onClick={() => triggerSecureCheckout(offering)}
                    className="w-full bg-sky-500 hover:bg-sky-600 text-white text-xs font-mono font-bold uppercase tracking-widest py-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 shadow-md shadow-sky-500/20"
                    data-testid={`checkout-button-${offering.id}`}
                  >
                    <span>Secure Checkout</span>
                    <ArrowRight className="h-4 w-4 text-white shrink-0" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Real Estate Professionals — WhatsApp Offer Strip */}
          <div className="mt-12 bg-gradient-to-r from-sky-600 to-cyan-500 rounded-2xl p-6 sm:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 shadow-lg shadow-sky-200" data-testid="realtor-offer-strip">
            <div className="space-y-2">
              <span className="text-[9px] font-mono tracking-widest text-sky-100 font-bold uppercase block">
                FOR REAL ESTATE PROFESSIONALS
              </span>
              <h3 className="text-xl font-black font-mono tracking-tight text-white uppercase">
                Your Own Professional Website for ₹8,260
              </h3>
              <p className="text-xs font-mono text-sky-100 leading-relaxed max-w-2xl">
                Stop sharing projects only on WhatsApp. Get a website with your own name and brand where buyers can see your projects and contact you directly. GST included — no hidden charges.
              </p>
            </div>
            <a
              href="https://wa.me/918291712551?text=Hi%20AEMS%20Marketing%2C%20I%20am%20a%20real%20estate%20professional%20and%20want%20a%20website%20for%20Rs.8260."
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 bg-white hover:bg-sky-50 text-sky-700 text-xs font-mono font-bold uppercase tracking-widest py-4 px-8 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 shadow-md"
              data-testid="whatsapp-offer-button"
            >
              <MessageCircle className="h-4 w-4 text-sky-600 shrink-0" />
              <span>Get My Website on WhatsApp</span>
            </a>
          </div>

          {/* Checkout transparency note */}
          <div className="mt-12 bg-sky-100/60 border border-sky-200 rounded-xl p-5 text-center max-w-3xl mx-auto">
            <span className="text-[9px] font-mono text-slate-400 uppercase tracking-widest font-bold block mb-1">
              SECURE ROUTING MEMORANDUM
            </span>
            <p className="text-xs font-mono text-slate-500 leading-relaxed">
              Payments for all offers are completed on our secured checkout page (Cashfree) at fredrickalphonso.com. The lead velocity diagnostics on this page run entirely in your browser — no data is stored on this site.
            </p>
          </div>

        </section>

        {/* --- MANIFESTO CONVICTIONS GRID --- */}
        <section id="manifesto" className="bg-sky-100/50 border-t border-sky-200 py-20 lg:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">

            <div className="space-y-4 max-w-3xl mb-16 lg:mb-24">
              <span className="text-xs uppercase font-mono tracking-[0.2em] text-sky-600 font-bold block">
                WHAT WE CAN BUILD FOR YOU
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 uppercase tracking-tight leading-none font-sans">
                The AEMS Digital Real Estate Manifesto
              </h2>
              <p className="text-slate-500 text-sm font-mono leading-relaxed">
                Modern websites built around clarity, credibility and meaningful enquiries. Our work centers on three absolute pillars.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  num: "01",
                  title: "Real Estate & Project Websites",
                  desc: "Project, property and lead-generation websites for builders, developers and real estate professionals — designed to showcase projects, strengthen credibility and generate buyer enquiries.",
                  icon: DollarSign
                },
                {
                  num: "02",
                  title: "Corporate & MSME Websites",
                  desc: "A professional digital presence for established and growing businesses — thoughtful design with practical development that works beautifully across devices.",
                  icon: Cpu
                },
                {
                  num: "03",
                  title: "Personal Brand Portfolios",
                  desc: "Distinguished single-page name portfolios for consultants, executives, celebrities and public-facing personalities — your name, your legacy, professionally presented.",
                  icon: Handshake
                }
              ].map((pillar) => {
                const IconComponent = pillar.icon;
                return (
                  <div
                    key={pillar.num}
                    className="bg-white border border-slate-200 rounded-xl p-8 space-y-6 hover:border-sky-400 hover:shadow-lg hover:shadow-sky-100 transition-all duration-300"
                  >
                    <div className="flex justify-between items-start">
                      <span className="text-3xl font-black text-sky-200 font-mono">
                        {pillar.num}
                      </span>
                      <div className="h-10 w-10 rounded-lg bg-sky-100 flex items-center justify-center text-sky-600">
                        <IconComponent className="h-5 w-5" />
                      </div>
                    </div>

                    <div className="space-y-2 font-mono">
                      <h3 className="text-sm font-bold tracking-widest text-slate-900 uppercase">
                        {pillar.title}
                      </h3>
                      <p className="text-xs text-slate-500 leading-relaxed font-medium">
                        {pillar.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </section>
        {/* --- FAQ --- */}
        <section id="faq" className="bg-white border-t border-sky-100 py-20 lg:py-32" data-testid="faq-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12">

            <div className="space-y-4 mb-12 lg:mb-16 text-center">
              <div className="inline-flex items-center space-x-2 bg-sky-50 border border-sky-200 rounded-full py-1 px-3 shadow-sm">
                <span className="h-2 w-2 rounded-full bg-sky-500" />
                <span className="text-[9px] font-mono tracking-widest text-sky-600 font-bold uppercase">
                  COMMON QUESTIONS
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 uppercase tracking-tight leading-none font-sans">
                Frequently Asked Questions
              </h2>
              <p className="text-slate-500 text-sm font-mono leading-relaxed max-w-2xl mx-auto">
                Simple, clear and professional — everything you need to know before starting your website project.
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-3" data-testid="faq-accordion">
              {[
                {
                  q: "What is included in the ₹7,000 + GST/year business website?",
                  a: "Everything you need to be online professionally: your own domain name, hosting, modern mobile-friendly design, and initial content for four pages — Home, About Us, Services and Contact Us. WhatsApp and call buttons are included on every page."
                },
                {
                  q: "How does the order and payment process work?",
                  a: "Six simple steps: choose your offer, send the short form, confirm the scope with us, pay through our secured Cashfree checkout, share proof on WhatsApp, and we build and deliver your website."
                },
                {
                  q: "Is GST included in the prices?",
                  a: "The Four-Page Business Website (₹7,000) and the 15-Projects Bulk Scheme (₹75,000) are priced + GST. The Personal Name Portfolio at ₹7,000 is all inclusive — even GST — and the Real Estate Professionals website at ₹8,260 is GST included with no hidden charges."
                },
                {
                  q: "Who are these websites for?",
                  a: "Builders, developers, real estate professionals, landowners, MSMEs, retailers, and personal brands such as celebrities, cricketers, entrepreneurs and public figures. Each website is designed around what brings your specific audience to enquire."
                },
                {
                  q: "Will my website work on mobile phones?",
                  a: "Yes. Every website is fully mobile responsive and lead-focused — your projects, enquiry buttons, WhatsApp and call buttons all work beautifully across phones, tablets and desktops."
                },
                {
                  q: "Do you support the website after launch?",
                  a: "Yes. We offer ongoing website maintenance — updates, content improvements and practical support — with direct Mumbai-based WhatsApp assistance from our team."
                }
              ].map((faq, idx) => (
                <AccordionItem
                  key={idx}
                  value={`faq-${idx}`}
                  className="bg-sky-50/60 border border-sky-100 rounded-xl px-5 data-[state=open]:border-sky-300 data-[state=open]:bg-white transition-colors"
                >
                  <AccordionTrigger
                    className="text-xs sm:text-sm font-mono font-bold text-slate-900 uppercase tracking-wide hover:no-underline hover:text-sky-600"
                    data-testid={`faq-question-${idx}`}
                  >
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-xs sm:text-sm font-mono text-slate-500 leading-relaxed"
                    data-testid={`faq-answer-${idx}`}
                  >
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-10 text-center">
              <a
                href="https://wa.me/918291712551?text=Hi%20AEMS%20Marketing%2C%20I%20have%20a%20question%20about%20your%20website%20offers."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-xs font-mono font-bold uppercase tracking-widest text-sky-600 hover:text-sky-700"
                data-testid="faq-whatsapp-link"
              >
                <MessageCircle className="h-4 w-4" />
                <span>Still have a question? WhatsApp us directly</span>
              </a>
            </div>

          </div>
        </section>
      </main>

      {/* --- FOOTER REGION --- */}
      <footer className="bg-sky-950 text-sky-100 pt-16 pb-12 border-t border-sky-900 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 pb-12 border-b border-sky-900">

            {/* Left Brand Col */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-gradient-to-tr from-sky-600 to-cyan-400 rounded-lg flex items-center justify-center">
                  <Terminal className="h-5 w-5 text-white shrink-0" />
                </div>
                <div>
                  <span className="text-white font-black text-lg tracking-tight block leading-tight font-mono uppercase">
                    AEMS Marketing
                  </span>
                  <span className="text-[10px] uppercase font-mono tracking-widest text-sky-400 block">
                    FOUNDED BY FREDRICK ALPHONSO
                  </span>
                </div>
              </div>
              <p className="text-xs text-sky-300 leading-relaxed font-mono font-medium">
                Website design & development for real estate professionals, builders, developers, MSMEs and personal brands — Mumbai-based, lead-focused, since 2016.
              </p>
            </div>

            {/* Quick links */}
            <div className="space-y-4 font-mono">
              <h4 className="text-xs uppercase font-mono tracking-widest text-sky-400 font-bold">
                Ecosystem Links
              </h4>
              <ul className="space-y-2 text-xs text-sky-300 font-medium">
                <li><a href="#about" className="hover:text-white transition-colors">Founder Bio</a></li>
                <li><a href="#work" className="hover:text-white transition-colors">Who We Serve</a></li>
                <li><a href="#lead-dashboard" className="hover:text-white transition-colors">Scoring Dashboard</a></li>
                <li><a href="#schemes" className="hover:text-white transition-colors">Offers & Schemes</a></li>
                <li><a href="#manifesto" className="hover:text-white transition-colors">The Manifesto</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>

            {/* Technical SEO Links */}
            <div className="space-y-4 font-mono">
              <h4 className="text-xs uppercase font-mono tracking-widest text-sky-400 font-bold">
                Technical Policies
              </h4>
              <ul className="space-y-2 text-xs text-sky-300 font-medium">
                <li><a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">XML Sitemap File</a></li>
                <li><a href="/robots.txt" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Robots.txt Crawl Policy</a></li>
              </ul>
            </div>

            {/* Mahim West Address */}
            <div className="space-y-4 font-mono">
              <h4 className="text-xs uppercase font-mono tracking-widest text-sky-400 font-bold">
                Mumbai Headquarters
              </h4>
              <ul className="space-y-3 text-xs text-sky-300 font-medium">
                <li className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 text-sky-400 shrink-0" />
                  <span>1st Floor, 4, Plot 173/177, Vakil Building, Raowadi, Gopi Tank Road (T H Kataria Marg), Near Mahim Post Office, Mahim West, Mumbai, Maharashtra 400016</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-sky-400 shrink-0" />
                  <a href="tel:+918291712551" className="hover:text-white transition-colors">+91 82917 12551</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-sky-400 shrink-0" />
                  <a href="mailto:ceo@aemsmarketing.com" className="hover:text-white transition-colors">ceo@aemsmarketing.com</a>
                </li>
              </ul>
            </div>

          </div>

          <div className="pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-sky-400 font-mono font-medium">
            <span>
              AEMS Marketing © 2016-2026. All rights reserved.
            </span>
            <div className="flex space-x-6">
              <span className="text-[10px] uppercase font-mono tracking-wider">
                Domain: learn.aemsmarketing.com
              </span>
              <span className="text-[10px] uppercase font-mono tracking-wider text-sky-300 font-bold">
                Design with purpose. Development that works.
              </span>
            </div>
          </div>

        </div>
      </footer>

      {/* --- LEAD CAPTURE DIAGNOSTICS DRAWER --- */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">

          {/* Backdrop */}
          <div
            onClick={() => setIsFormOpen(false)}
            className="absolute inset-0 bg-sky-950/60 backdrop-blur-md cursor-pointer animate-in fade-in duration-200"
          />

          {/* Dialog Container */}
          <div
            className="relative w-full max-w-[550px] bg-white rounded-xl shadow-2xl overflow-hidden border border-sky-200 z-10 animate-in zoom-in-95 duration-300"
            data-testid="lead-capture-modal"
          >
            {/* Header */}
            <div className="bg-sky-50 border-b border-sky-100 px-6 py-5 flex justify-between items-center">
              <div className="space-y-1 font-mono">
                <span className="text-[9px] font-mono tracking-widest text-sky-600 font-bold uppercase">
                  AEMS SCORING DIAGNOSTICS PROTOCOL
                </span>
                <h3 className="text-base font-bold tracking-tight text-slate-900 uppercase">
                  Map Your Real Estate Node
                </h3>
              </div>
              <button
                onClick={() => setIsFormOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-sky-100 transition-colors"
                data-testid="modal-close-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Progress */}
            <div className="w-full h-1 bg-sky-100 relative">
              <div
                className="absolute h-full bg-sky-500 transition-all duration-300"
                style={{ width: `${(Math.min(formStep, 3) / 3) * 100}%` }}
                data-testid="lead-form-step-indicator"
              />
            </div>

            {/* Steps form */}
            <form onSubmit={handleFormSubmit} className="p-6 sm:p-8 space-y-6">

              {formStep === 1 && (
                <div className="space-y-4 font-mono animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="space-y-1">
                    <h4 className="text-xs font-bold text-sky-600 uppercase tracking-widest">
                      Step 1: Core Identity Matrix
                    </h4>
                    <p className="text-[11px] text-slate-400">
                      Please provide valid direct communication parameters to synchronize your lead scores.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Full Name</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your full name"
                        className="w-full px-4 py-3 border border-slate-200 bg-sky-50 text-slate-900 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent font-medium"
                        data-testid="lead-form-input-name"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Email Address</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="you@example.com"
                        className="w-full px-4 py-3 border border-slate-200 bg-sky-50 text-slate-900 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent font-medium"
                        data-testid="lead-form-input-email"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">WhatsApp Contact Number</label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+91 00000 00000"
                        className="w-full px-4 py-3 border border-slate-200 bg-sky-50 text-slate-900 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent font-medium"
                        data-testid="lead-form-input-phone"
                      />
                    </div>
                  </div>
                </div>
              )}

              {formStep === 2 && (
                <div className="space-y-4 font-mono animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="space-y-1">
                    <h4 className="text-xs font-bold text-sky-600 uppercase tracking-widest">
                      Step 2: Calibrate Scope & Region
                    </h4>
                    <p className="text-[11px] text-slate-400">
                      Map your operating geography and ecosystem category to calibrate the diagnostic.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Operating Region</label>
                      <select
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 bg-sky-50 text-slate-900 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent font-medium"
                        data-testid="lead-form-input-city"
                      >
                        <option value="Mumbai MMR">South Mumbai (SOBO) Core</option>
                        <option value="Mumbai Central">Lower Parel & Worli Sync Hub</option>
                        <option value="Mahim">Mahim & Dadar Hub</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Ecosystem Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 bg-sky-50 text-slate-900 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent font-medium"
                        data-testid="lead-form-input-category"
                      >
                        <option value="broker">Real Estate Broker</option>
                        <option value="developer">Builder / Developer</option>
                        <option value="investor">Institutional Investor</option>
                        <option value="buyer">SOBO Luxury Home Buyer</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Onboarding Priority Timeline</label>
                      <div className="grid grid-cols-3 gap-2">
                        {["Immediate", "1-3 Months", "Planning Phase"].map((timeVal) => (
                          <button
                            key={timeVal}
                            type="button"
                            onClick={() => setFormData({ ...formData, timeline: timeVal })}
                            className={`py-2.5 rounded-lg text-[10px] font-mono font-bold border transition-all duration-200 ${
                              formData.timeline === timeVal
                                ? "bg-sky-100 border-sky-500 text-sky-700 shadow-inner"
                                : "bg-sky-50 border-slate-200 text-slate-500 hover:bg-sky-100"
                            }`}
                            data-testid={`timeline-${timeVal.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          >
                            {timeVal}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {formStep === 3 && (
                <div className="space-y-4 font-mono animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="space-y-1">
                    <h4 className="text-xs font-bold text-sky-600 uppercase tracking-widest">
                      Step 3: Verification Protocol
                    </h4>
                    <p className="text-[11px] text-slate-400">
                      Confirm your details to generate your lead score and prepare your enquiry email.
                    </p>
                  </div>

                  <div className="bg-sky-50 border border-sky-100 p-4 rounded-xl space-y-3">
                    <div className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-sky-500 shrink-0 mt-0.5" />
                      <span className="text-[10px] text-slate-600 leading-relaxed font-semibold">
                        I want a custom consultation from Fredrick Alphonso and the AEMS Marketing team.
                      </span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-sky-500 shrink-0 mt-0.5" />
                      <span className="text-[10px] text-slate-600 leading-relaxed font-semibold">
                        I agree to be contacted on WhatsApp / call regarding my requirement.
                      </span>
                    </div>
                  </div>

                  <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg flex items-center space-x-2 text-[10px] text-slate-500">
                    <Lock className="h-4 w-4 text-sky-500 shrink-0" />
                    <span>Submitting opens your own email app with the enquiry pre-filled — nothing is stored on this site.</span>
                  </div>
                </div>
              )}

              {formStep === 4 && (
                <div className="text-center space-y-6 py-6 font-mono animate-in zoom-in-95 duration-300">
                  <div className="mx-auto h-16 w-16 bg-sky-100 border border-sky-300 rounded-full flex items-center justify-center shadow-lg">
                    <ShieldCheck className="h-10 w-10 text-sky-500" />
                  </div>

                  <div className="space-y-2">
                    <h4 className="text-lg font-bold tracking-tight text-slate-900 uppercase">
                      Diagnostics Index Calculated!
                    </h4>
                    <p className="text-xs text-slate-500 max-w-sm mx-auto font-medium">
                      Welcome to Fredrick Alphonso&apos;s AEMS Real Estate Ecosystem, <strong>{formData.name}</strong>. Your pipeline score:
                    </p>
                  </div>

                  <div className="bg-sky-50 border border-sky-200 p-6 rounded-2xl inline-block max-w-sm mx-auto space-y-2">
                    <span className="text-[9px] font-mono tracking-widest text-slate-400 uppercase block font-bold">
                      AEMS SYSTEM VELOCITY VALUE
                    </span>
                    <div className="text-4xl font-black font-mono text-sky-600" data-testid="lead-form-final-score">
                      {leadScore} / 100
                    </div>
                    <span className="text-[10px] font-bold text-cyan-700 bg-cyan-50 border border-cyan-200 px-2.5 py-1 rounded-full">
                      HIGH CONVERSION COMPATIBILITY
                    </span>
                  </div>

                  <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">
                    Your enquiry email has been prepared — press send in your email app, or reach us directly on WhatsApp at <strong>+91 82917 12551</strong>.
                  </p>

                  <div className="pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setFormStep(1);
                        setIsFormOpen(false);
                      }}
                      className="bg-sky-500 hover:bg-sky-600 text-white text-xs font-mono font-bold uppercase tracking-widest py-3.5 px-8 rounded-lg shadow-md shadow-sky-500/25 transition-colors"
                      data-testid="lead-form-restart-button"
                    >
                      Complete Diagnostics
                    </button>
                  </div>
                </div>
              )}

              {/* Form Nav controls */}
              {formStep < 4 && (
                <div className="flex justify-between items-center pt-4 border-t border-sky-100">
                  {formStep > 1 ? (
                    <button
                      type="button"
                      onClick={() => setFormStep(prev => prev - 1)}
                      className="text-slate-400 hover:text-slate-900 text-xs font-mono font-bold uppercase tracking-wider flex items-center space-x-1"
                      data-testid="lead-form-back-button"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span>Back</span>
                    </button>
                  ) : (
                    <div />
                  )}

                  {formStep < 3 ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (formStep === 1 && (!formData.name || !formData.email || !formData.phone)) {
                          toast.error("Contact details missing", {
                            description: "Please complete identity core parameters to proceed."
                          });
                          return;
                        }
                        setFormStep(prev => prev + 1);
                      }}
                      className="bg-sky-100 hover:bg-sky-200 border border-sky-200 text-slate-800 text-xs font-mono font-bold uppercase tracking-wider px-5 py-3 rounded-lg shadow-sm transition-colors flex items-center space-x-1"
                      data-testid="lead-form-next-button"
                    >
                      <span>Next Matrix</span>
                      <ChevronRight className="h-4 w-4 text-sky-600" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="bg-sky-500 hover:bg-sky-600 text-white text-xs font-mono font-bold uppercase tracking-widest px-6 py-3 rounded-lg shadow-md shadow-sky-500/25 transition-colors flex items-center space-x-1"
                      data-testid="lead-form-submit-button"
                    >
                      <span>Analyze System Velocity</span>
                      <Cpu className="h-4 w-4 animate-spin text-white shrink-0" />
                    </button>
                  )}
                </div>
              )}

            </form>
          </div>
        </div>
      )}
    </div>
  );
}
